/**
 * RTWO Event Bus & Message Broker — Domain Models
 * Phase B.5 | Module ID: RTWO-EBUS-001
 * Version: 1.0.0-phaseB5
 *
 * Strongly typed domain models for the RTWO Event Bus & Message Broker.
 * Defines the inter-module communication layer connecting all RTWO engines.
 *
 * Truthful Data Policy:
 * - No fake events, messages, retries, queues, delivery metrics, or event traffic.
 * - Event Bus is NOT operational — architecture phase only.
 * - All runtime values are null until real event sources are connected.
 *
 * Source: Event Bus (RTWO-EBUS-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type EventId = string;
export type TopicId = string;
export type PublisherId = string;
export type SubscriberId = string;
export type RouteId = string;
export type DeliveryId = string;
export type DeadLetterId = string;
export type MetadataId = string;
export type EBusTimestamp = number | null;

export const EVENT_BUS_VERSION = {
  moduleId: 'RTWO-EBUS-001',
  moduleName: 'Event Bus & Message Broker',
  version: '1.0.0-phaseB5',
  phase: 'Phase B.5 — Event Bus Federation Runtime Activation',
  status: 'Federation Channels Connected — Event Streams Empty Until Real Runtime Activity',
  productionReadiness: 'Runtime Federation Active — No Fabricated Events or Delivery Metrics',
  policy:
    'Truthful Data Policy Enforced — No Fake Events, Messages, Retries, Queues, Delivery Metrics, or Event Traffic',
} as const;

// ─────────────────────────────────────────────
// EVENT TYPES
// ─────────────────────────────────────────────

export type RTWOEventType =
  | 'block_produced'
  | 'block_finalized'
  | 'transaction_submitted'
  | 'transaction_confirmed'
  | 'transaction_rejected'
  | 'validator_joined'
  | 'validator_removed'
  | 'validator_slashed'
  | 'consensus_round_started'
  | 'consensus_round_completed'
  | 'peer_connected'
  | 'peer_disconnected'
  | 'ledger_state_updated'
  | 'wallet_created'
  | 'wallet_activated'
  | 'node_registered'
  | 'node_status_changed'
  | 'rpc_request_received'
  | 'index_job_created'
  | 'index_job_completed'
  | 'system_registry_updated'
  | 'module_status_changed'
  | 'engine_initialized'
  | 'engine_shutdown';

export type RTWOEventPriority = 'critical' | 'high' | 'normal' | 'low';

// ─────────────────────────────────────────────
// EVENT LIFECYCLE
// ─────────────────────────────────────────────

export type RTWOEventStatus =
  | 'created'
  | 'validated'
  | 'published'
  | 'routed'
  | 'delivered'
  | 'acknowledged'
  | 'completed'
  | 'failed'
  | 'dead_letter';

export interface EventLifecycleStage {
  status: RTWOEventStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealData: boolean;
  truthfulNote: string;
}

export const EVENT_LIFECYCLE: EventLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description:
      'Event created and registered in the event registry. Event ID assigned. Source module recorded.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real event source module to be connected. No fake events created.',
  },
  {
    status: 'validated',
    label: 'Validated',
    description: 'Event schema and payload validated against the registered event type definition.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real event schema registry. No fake validation results.',
  },
  {
    status: 'published',
    label: 'Published',
    description: 'Event published to the event bus topic. Available for routing to subscribers.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real publisher registration. No fake published events.',
  },
  {
    status: 'routed',
    label: 'Routed',
    description: 'Event routed to the appropriate subscriber queues based on routing policy.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real routing engine. No fake routing decisions.',
  },
  {
    status: 'delivered',
    label: 'Delivered',
    description: 'Event delivered to the target subscriber module. Awaiting acknowledgement.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real delivery infrastructure. No fake delivery confirmations.',
  },
  {
    status: 'acknowledged',
    label: 'Acknowledged',
    description: 'Subscriber acknowledged receipt and processing of the event.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real subscriber acknowledgement. No fake ACKs.',
  },
  {
    status: 'completed',
    label: 'Completed',
    description: 'Event lifecycle completed successfully. Event archived.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal success state. Requires real end-to-end event pipeline.',
  },
  {
    status: 'failed',
    label: 'Failed',
    description:
      'Event delivery failed. Retry manager will attempt redelivery based on retry policy.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real retry infrastructure. No fake retry counts.',
  },
  {
    status: 'dead_letter',
    label: 'Dead Letter',
    description: 'Event moved to dead letter queue after exhausting retry attempts.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal failure state. Dead letter queue is empty — no fake dead letters.',
  },
];

export const EVENT_STATUS_LABELS: Record<RTWOEventStatus, string> = {
  created: 'Created',
  validated: 'Validated',
  published: 'Published',
  routed: 'Routed',
  delivered: 'Delivered',
  acknowledged: 'Acknowledged',
  completed: 'Completed',
  failed: 'Failed',
  dead_letter: 'Dead Letter',
};

// ─────────────────────────────────────────────
// TOPIC STATUS
// ─────────────────────────────────────────────

export type RTWOTopicStatus =
  | 'architecture_ready'
  | 'waiting_for_publishers'
  | 'waiting_for_subscribers'
  | 'active'
  | 'inactive'
  | 'deprecated';

export type RTWOPublisherStatus =
  'architecture_ready' | 'not_registered' | 'registered' | 'active' | 'inactive' | 'error';

export type RTWOSubscriberStatus =
  'architecture_ready' | 'not_registered' | 'registered' | 'active' | 'inactive' | 'error';

export type RTWORouteStatus =
  'architecture_ready' | 'not_configured' | 'configured' | 'active' | 'inactive' | 'error';

export type RTWODeliveryStatus = 'pending' | 'in_flight' | 'delivered' | 'failed' | 'dead_letter';

export type RTWODeadLetterStatus =
  'empty' | 'waiting_for_real_events' | 'has_entries' | 'processing';

export type RTWORoutingPolicy =
  'broadcast' | 'unicast' | 'multicast' | 'round_robin' | 'priority_based' | 'content_based';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOEvent
// ─────────────────────────────────────────────

export interface RTWOEvent {
  /** Unique identifier for this event */
  eventId: EventId;
  /** Type of event */
  eventType: RTWOEventType;
  /** Module that originated this event */
  sourceModule: string;
  /** Target module (null = broadcast) */
  targetModule: string | null;
  /** Reference to the event payload (not stored inline) */
  payloadReference: string | null;
  /** Event priority */
  priority: RTWOEventPriority;
  /** Current lifecycle status */
  status: RTWOEventStatus;
  /** Timestamp when event was created */
  createdAt: EBusTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOEventTopic
// ─────────────────────────────────────────────

export interface RTWOEventTopic {
  /** Unique identifier for this topic */
  topicId: TopicId;
  /** Topic name (e.g., rtwo.block.produced) */
  topicName: string;
  /** Human-readable description */
  description: string;
  /** Current topic status */
  status: RTWOTopicStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOPublisher
// ─────────────────────────────────────────────

export interface RTWOPublisher {
  /** Unique identifier for this publisher */
  publisherId: PublisherId;
  /** Module ID of the publishing module */
  moduleId: string;
  /** Event types this publisher can emit */
  supportedEvents: RTWOEventType[];
  /** Current publisher status */
  status: RTWOPublisherStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOSubscriber
// ─────────────────────────────────────────────

export interface RTWOSubscriber {
  /** Unique identifier for this subscriber */
  subscriberId: SubscriberId;
  /** Module ID of the subscribing module */
  moduleId: string;
  /** Topic IDs this subscriber is registered for */
  subscribedTopics: TopicId[];
  /** Current subscriber status */
  status: RTWOSubscriberStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOEventRoute
// ─────────────────────────────────────────────

export interface RTWOEventRoute {
  /** Unique identifier for this route */
  routeId: RouteId;
  /** Source module for this route */
  sourceModule: string;
  /** Destination module for this route */
  destinationModule: string;
  /** Routing policy applied */
  routingPolicy: RTWORoutingPolicy;
  /** Current route status */
  status: RTWORouteStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOEventDelivery
// ─────────────────────────────────────────────

export interface RTWOEventDelivery {
  /** Unique identifier for this delivery record */
  deliveryId: DeliveryId;
  /** Reference to the event being delivered */
  eventId: EventId;
  /** Current delivery status */
  deliveryStatus: RTWODeliveryStatus;
  /** Number of delivery attempts */
  retryCount: number;
  /** Whether this delivery is in the dead letter queue */
  deadLetter: boolean;
  /** Timestamp of last delivery attempt */
  timestamp: EBusTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWODeadLetterQueue
// ─────────────────────────────────────────────

export interface RTWODeadLetterQueue {
  /** Unique identifier for this dead letter entry */
  queueId: DeadLetterId;
  /** Reason the event was dead-lettered */
  reason: string | null;
  /** Reference to the original event */
  eventReference: EventId | null;
  /** Current dead letter queue status */
  status: RTWODeadLetterStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOEventMetadata
// ─────────────────────────────────────────────

export interface RTWOEventMetadata {
  /** Unique identifier for this metadata record */
  metadataId: MetadataId;
  /** Reference to the event */
  eventId: EventId;
  /** Event bus version */
  version: string;
  /** Schema version for the event type */
  schemaVersion: string;
  /** Correlation ID for tracing related events */
  correlationId: string | null;
  /** Metadata status */
  status: 'architecture_ready' | 'waiting_for_real_events' | 'active';
}

// ─────────────────────────────────────────────
// TOPIC REGISTRY — Pre-defined topics
// ─────────────────────────────────────────────

export interface TopicDefinition {
  topicId: TopicId;
  topicName: string;
  description: string;
  sourceModules: string[];
  eventTypes: RTWOEventType[];
  status: RTWOTopicStatus;
}

export const RTWO_TOPIC_REGISTRY: TopicDefinition[] = [
  {
    topicId: 'topic-block',
    topicName: 'rtwo.block',
    description: 'Block lifecycle events from Blockchain Protocol Engine and Consensus Engine',
    sourceModules: ['blockchain-protocol-engine', 'consensus-engine'],
    eventTypes: ['block_produced', 'block_finalized'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-transaction',
    topicName: 'rtwo.transaction',
    description: 'Transaction lifecycle events from Ledger Engine and Wallet Engine',
    sourceModules: ['ledger-state-engine', 'wallet-engine'],
    eventTypes: ['transaction_submitted', 'transaction_confirmed', 'transaction_rejected'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-validator',
    topicName: 'rtwo.validator',
    description: 'Validator lifecycle events from Validator Infrastructure',
    sourceModules: ['validator-infrastructure'],
    eventTypes: ['validator_joined', 'validator_removed', 'validator_slashed'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-consensus',
    topicName: 'rtwo.consensus',
    description: 'Consensus round events from Consensus Engine',
    sourceModules: ['consensus-engine'],
    eventTypes: ['consensus_round_started', 'consensus_round_completed'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-network',
    topicName: 'rtwo.network',
    description: 'P2P network events from P2P Network Engine',
    sourceModules: ['p2p-network-engine'],
    eventTypes: ['peer_connected', 'peer_disconnected'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-ledger',
    topicName: 'rtwo.ledger',
    description: 'Ledger state events from Ledger & State Engine',
    sourceModules: ['ledger-state-engine'],
    eventTypes: ['ledger_state_updated'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-wallet',
    topicName: 'rtwo.wallet',
    description: 'Wallet lifecycle events from Wallet Engine',
    sourceModules: ['wallet-engine'],
    eventTypes: ['wallet_created', 'wallet_activated'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-node',
    topicName: 'rtwo.node',
    description: 'Node infrastructure events from Node Infrastructure',
    sourceModules: ['node-infrastructure'],
    eventTypes: ['node_registered', 'node_status_changed'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-rpc',
    topicName: 'rtwo.rpc',
    description: 'RPC platform events from RPC Platform',
    sourceModules: ['rpc-platform'],
    eventTypes: ['rpc_request_received'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-indexer',
    topicName: 'rtwo.indexer',
    description: 'Indexer events from Indexer Engine',
    sourceModules: ['indexer-engine'],
    eventTypes: ['index_job_created', 'index_job_completed'],
    status: 'architecture_ready',
  },
  {
    topicId: 'topic-system',
    topicName: 'rtwo.system',
    description: 'System-level events from Core Engine and System Registry',
    sourceModules: ['core-engine', 'system-registry'],
    eventTypes: [
      'system_registry_updated',
      'module_status_changed',
      'engine_initialized',
      'engine_shutdown',
    ],
    status: 'architecture_ready',
  },
];

// ─────────────────────────────────────────────
// PUBLISHER REGISTRY — Pre-defined publishers
// ─────────────────────────────────────────────

export interface PublisherDefinition {
  publisherId: PublisherId;
  moduleId: string;
  moduleName: string;
  supportedEvents: RTWOEventType[];
  status: RTWOPublisherStatus;
}

export const RTWO_PUBLISHER_REGISTRY: PublisherDefinition[] = [
  {
    publisherId: 'pub-core',
    moduleId: 'core-engine',
    moduleName: 'Core Engine',
    supportedEvents: ['engine_initialized', 'engine_shutdown', 'module_status_changed'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-protocol',
    moduleId: 'blockchain-protocol-engine',
    moduleName: 'Blockchain Protocol Engine',
    supportedEvents: ['block_produced'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-consensus',
    moduleId: 'consensus-engine',
    moduleName: 'Consensus Engine',
    supportedEvents: ['block_finalized', 'consensus_round_started', 'consensus_round_completed'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-ledger',
    moduleId: 'ledger-state-engine',
    moduleName: 'Ledger & State Engine',
    supportedEvents: ['ledger_state_updated', 'transaction_confirmed', 'transaction_rejected'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-storage',
    moduleId: 'storage-abstraction-layer',
    moduleName: 'Storage Abstraction Layer',
    supportedEvents: [],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-p2p',
    moduleId: 'p2p-network-engine',
    moduleName: 'P2P Network Engine',
    supportedEvents: ['peer_connected', 'peer_disconnected'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-registry',
    moduleId: 'system-registry',
    moduleName: 'System Registry',
    supportedEvents: ['system_registry_updated'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-crypto',
    moduleId: 'cryptography-identity-engine',
    moduleName: 'Cryptography & Identity Engine',
    supportedEvents: [],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-wallet',
    moduleId: 'wallet-engine',
    moduleName: 'Wallet Engine',
    supportedEvents: ['wallet_created', 'wallet_activated', 'transaction_submitted'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-validator',
    moduleId: 'validator-infrastructure',
    moduleName: 'Validator Infrastructure',
    supportedEvents: ['validator_joined', 'validator_removed', 'validator_slashed'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-node',
    moduleId: 'node-infrastructure',
    moduleName: 'Node Infrastructure',
    supportedEvents: ['node_registered', 'node_status_changed'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-rpc',
    moduleId: 'rpc-platform',
    moduleName: 'RPC Platform',
    supportedEvents: ['rpc_request_received'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-indexer',
    moduleId: 'indexer-engine',
    moduleName: 'Indexer Engine',
    supportedEvents: ['index_job_created', 'index_job_completed'],
    status: 'architecture_ready',
  },
  {
    publisherId: 'pub-truthful',
    moduleId: 'truthful-status-manager',
    moduleName: 'Truthful Status Manager',
    supportedEvents: ['module_status_changed'],
    status: 'architecture_ready',
  },
];

// ─────────────────────────────────────────────
// SERVICE IDs
// ─────────────────────────────────────────────

export const EVENT_BUS_SERVICE_IDS = {
  EVENT_REGISTRY: 'ebus-svc-event-registry',
  TOPIC_REGISTRY: 'ebus-svc-topic-registry',
  PUBLISHER_REGISTRY: 'ebus-svc-publisher-registry',
  SUBSCRIBER_REGISTRY: 'ebus-svc-subscriber-registry',
  ROUTING_ENGINE: 'ebus-svc-routing-engine',
  DELIVERY_ENGINE: 'ebus-svc-delivery-engine',
  RETRY_MANAGER: 'ebus-svc-retry-manager',
  DEAD_LETTER_QUEUE: 'ebus-svc-dead-letter-queue',
  METADATA_SERVICE: 'ebus-svc-metadata',
  EVENT_BUS_REGISTRY: 'ebus-svc-bus-registry',
  EVENT_PUBLISHER: 'ebus-svc-publisher',
  READINESS_SERVICE: 'ebus-svc-readiness',
} as const;

export type EBusServiceId = (typeof EVENT_BUS_SERVICE_IDS)[keyof typeof EVENT_BUS_SERVICE_IDS];

// ─────────────────────────────────────────────
// ENGINE STATUS
// ─────────────────────────────────────────────

export type RTWOEventBusStatus =
  | 'not_initialized'
  | 'initializing'
  | 'structure_ready'
  | 'waiting_for_real_event_sources'
  | 'error'
  | 'shutdown';

// ─────────────────────────────────────────────
// INTEGRATION MAP
// ─────────────────────────────────────────────

export interface EBusIntegration {
  moduleId: string;
  moduleName: string;
  phase: string;
  role: 'publisher' | 'subscriber' | 'both';
  connectionStatus: string;
  eventTypes: string[];
}

const FEDERATION_CHANNEL_CONNECTED = 'Federation Channel Connected — Event Stream Empty';

export const EVENT_BUS_INTEGRATIONS: EBusIntegration[] = [
  {
    moduleId: 'core-engine',
    moduleName: 'Core Engine',
    phase: 'Phase 1',
    role: 'both',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['engine_initialized', 'engine_shutdown', 'module_status_changed'],
  },
  {
    moduleId: 'blockchain-protocol-engine',
    moduleName: 'Blockchain Protocol Engine',
    phase: 'Phase 3',
    role: 'publisher',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['block_produced'],
  },
  {
    moduleId: 'consensus-engine',
    moduleName: 'Consensus Engine',
    phase: 'Phase 4',
    role: 'both',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['block_finalized', 'consensus_round_started', 'consensus_round_completed'],
  },
  {
    moduleId: 'ledger-state-engine',
    moduleName: 'Ledger & State Engine',
    phase: 'Phase 5',
    role: 'both',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['ledger_state_updated', 'transaction_confirmed'],
  },
  {
    moduleId: 'storage-abstraction-layer',
    moduleName: 'Storage Abstraction Layer',
    phase: 'Phase 5',
    role: 'subscriber',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: [],
  },
  {
    moduleId: 'p2p-network-engine',
    moduleName: 'P2P Network Engine',
    phase: 'Phase 6',
    role: 'publisher',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['peer_connected', 'peer_disconnected'],
  },
  {
    moduleId: 'system-registry',
    moduleName: 'System Registry',
    phase: 'Phase 6.5',
    role: 'both',
    connectionStatus: 'Architecture Ready',
    eventTypes: ['system_registry_updated'],
  },
  {
    moduleId: 'cryptography-identity-engine',
    moduleName: 'Cryptography & Identity Engine',
    phase: 'Phase 7',
    role: 'subscriber',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: [],
  },
  {
    moduleId: 'wallet-engine',
    moduleName: 'Wallet Engine',
    phase: 'Phase 8',
    role: 'both',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['wallet_created', 'wallet_activated', 'transaction_submitted'],
  },
  {
    moduleId: 'validator-infrastructure',
    moduleName: 'Validator Infrastructure',
    phase: 'Phase 9',
    role: 'publisher',
    connectionStatus: 'Architecture Ready',
    eventTypes: ['validator_joined', 'validator_removed', 'validator_slashed'],
  },
  {
    moduleId: 'node-infrastructure',
    moduleName: 'Node Infrastructure',
    phase: 'Phase 10',
    role: 'publisher',
    connectionStatus: 'Architecture Ready',
    eventTypes: ['node_registered', 'node_status_changed'],
  },
  {
    moduleId: 'rpc-platform',
    moduleName: 'RPC Platform',
    phase: 'Phase 11',
    role: 'both',
    connectionStatus: FEDERATION_CHANNEL_CONNECTED,
    eventTypes: ['rpc_request_received'],
  },
  {
    moduleId: 'indexer-engine',
    moduleName: 'Indexer Engine',
    phase: 'Phase 12',
    role: 'both',
    connectionStatus: 'Architecture Ready',
    eventTypes: ['index_job_created', 'index_job_completed'],
  },
  {
    moduleId: 'truthful-status-manager',
    moduleName: 'Truthful Status Manager',
    phase: 'Phase 1',
    role: 'both',
    connectionStatus: 'Architecture Ready',
    eventTypes: ['module_status_changed'],
  },
];
